import { useCallback, useRef, useState } from 'react';

const DEFAULT_STORAGE_KEY = 'sidebar.collapsed';

interface SidebarCollapsedState {
  readonly collapsed: boolean;
  readonly toggle: () => void;
  readonly setCollapsed: (value: boolean) => void;
}

function readStoredCollapsed(storageKey: string): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

function persistCollapsed(storageKey: string, value: boolean): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    return;
  }
}

export function useSidebarCollapsed(
  storageKey: string = DEFAULT_STORAGE_KEY,
): SidebarCollapsedState {
  const [collapsed, setCollapsedState] = useState(() => readStoredCollapsed(storageKey));
  const collapsedRef = useRef(collapsed);

  const setCollapsed = useCallback(
    (value: boolean) => {
      collapsedRef.current = value;
      setCollapsedState(value);
      persistCollapsed(storageKey, value);
    },
    [storageKey],
  );

  const toggle = useCallback(() => {
    setCollapsed(!collapsedRef.current);
  }, [setCollapsed]);

  return { collapsed, toggle, setCollapsed };
}
